import type { AxiosResponse } from "axios";

export type ResolvedAttachment = {
  blob: Blob;
  fileName: string;
  contentType: string;
};

/**
 * Defensively resolves an attachment "content" endpoint response into a usable blob.
 *
 * The BE OpenAPI spec declares these endpoints as application/json wrapping a
 * `Stream` object (canRead/canWrite/length/...) — Swashbuckle's reflected shape
 * for a raw .NET Stream property, almost certainly not what's actually served.
 * Unconfirmed in Scalar as of this writing, so this handles both outcomes:
 *
 * 1. Raw binary (expected) — filename/contentType come from response headers.
 * 2. JSON body (BE bug, or the spec is literally accurate) — surface a clear
 *    error instead of silently trying to preview it as a file.
 */
export async function resolveAttachmentContent(
  response: AxiosResponse<Blob>,
  fallbackFileName: string,
): Promise<ResolvedAttachment> {
  const contentType =
    response.headers["content-type"] ?? "application/octet-stream";

  if (contentType.includes("application/json")) {
    const text = await response.data.text();
    let parsedFileName = fallbackFileName;
    let parsedContentType = "application/octet-stream";

    try {
      const parsed = JSON.parse(text);
      parsedFileName = parsed.fileName ?? parsedFileName;
      parsedContentType = parsed.contentType ?? parsedContentType;
    } catch {
      // not even valid JSON — nothing more to infer
    }

    throw new Error(
      `Attachment content endpoint returned JSON instead of a file body ` +
        `(fileName: ${parsedFileName}, contentType: ${parsedContentType}). ` +
        `Matches the BE spec's declared "Stream" schema — confirm with Benedict before this endpoint is usable.`,
    );
  }

  const contentDisposition = response.headers["content-disposition"] as
    string | undefined;
  const fileNameMatch = contentDisposition?.match(
    /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i,
  );
  const fileName = fileNameMatch?.[1]
    ? decodeURIComponent(fileNameMatch[1])
    : fallbackFileName;

  return { blob: response.data, fileName, contentType };
}
